<template>
    <div>
        <!-- Dialog -->
        <el-dialog
            :visible.sync="visible"
            :before-close="handleClose"
            title="A2P Campaign Review 📝"
        >
            <!-- Content -->
            <el-form>
                <div class="row">
                    <!-- Rejection Reason Alerts -->
                    <div v-if="campaign_details?.errors && campaign_details.errors.length > 0" class="col-12 mb-2">
                        <el-alert
                            v-for="error in campaign_details.errors"
                            :key="error.error_code"
                            show-icon
                            type="error"
                            :closable="false"
                            class="mb-2"
                        >
                            <span class="text-dark">
                                {{ error.error_code }}: <span class="font-weight-bold">{{ fixToUcFirst(error.fields[0]) }}</span> - {{ error.description }}
                            </span>
                        </el-alert>
                    </div>
                    
                    <div class="col-12">
                        <b>Use Case</b>
                        <br/>
                        <p class="mt-2">{{ fixToUcFirst(campaign_details?.use_case) }}</p>
                    </div>
                    <div class="col-12 mt-2">
                        <b>Description</b>
                        <br/>
                        <p class="mt-2">{{ campaign_details?.description }}</p>
                    </div>
                    <div class="col-12 mt-2">
                        <b>Message Flow</b>
                        <br/>
                        <p class="mt-2">{{ campaign_details?.message_flow }}</p>
                    </div>
                    <div class="col-12 mt-2">
                        <b>Message Sample #1</b>
                        <br/>
                        <p class="mt-2">{{ campaign_details?.message_samples[0] }}</p>
                    </div>
                    <div class="col-12 mt-2">
                        <b>Message Sample #2</b>
                        <br/>
                        <p class="mt-2">{{ campaign_details?.message_samples[1] }}</p>
                    </div>
                    <div class="col-6 mt-2">
                        <b>Has Embedded Links</b>
                        <br/>
                        <b class="label"
                           :class="campaign_details?.has_embedded_links ? 'green-600' : 'red-600'">
                            <span class="mt-2">{{ campaign_details?.has_embedded_links ? "Yes" : "No" }}</span>
                        </b>
                    </div>
                    <div class="col-6 mt-2">
                        <b>Has Embedded Phone Numbers</b>
                        <br/>
                        <b class="label"
                           :class="campaign_details?.has_embedded_phone ? 'green-600' : 'red-600'">
                            <span class="mt-2">{{ campaign_details?.has_embedded_phone ? "Yes" : "No" }}</span>
                        </b>
                    </div>
                    <div class="col-6 mt-2">
                        <b>Has content related to Direct Lending or other Loan Arrangement.</b>
                        <br/>
                        <b class="label"
                           :class="campaign_details?.direct_lending ? 'green-600' : 'red-600'">
                            <span class="mt-2">{{ campaign_details?.direct_lending ? "Yes" : "No" }}</span>
                        </b>
                    </div>
                    <div class="col-6 mt-2">
                        <b>Has age-gated content.</b>
                        <br/>
                        <b class="label"
                           :class="campaign_details?.age_gated ? 'green-600' : 'red-600'">
                            <span class="mt-2">{{ campaign_details?.age_gated ? "Yes" : "No" }}</span>
                        </b>
                    </div>
                    <div class="col-6 mt-2">
                        <b>Privacy Policy URL</b>
                        <br/>
                        <b class="text-blue">
                            <a v-if="campaign_details?.privacy_policy_url" :href="campaign_details?.privacy_policy_url" target="_blank">{{ campaign_details?.privacy_policy_url }}</a>
                            <span v-else>N/A</span>
                        </b> 
                    </div>
                    <div class="col-6 mt-2">
                        <b>Terms and Conditions URL</b>
                        <br/>
                        <b class="text-blue">
                            <a v-if="campaign_details?.terms_and_conditions_url" :href="campaign_details?.terms_and_conditions_url" target="_blank">{{ campaign_details?.terms_and_conditions_url }}</a>
                            <span v-else>N/A</span>
                        </b>
                    </div>
                </div>
                <div class="row mt-5">
                    <div class="ml-auto col-4">
                        <el-button
                            type="danger"
                            class="btn-block"
                            @click="openFailureReasonDialog"
                            :disabled="disableRejectButton"
                            :loading="reject_button_loading"
                        >
                            Reject
                        </el-button>
                    </div>
                    <div class="col-4">
                        <el-button
                            type="primary"
                            class="btn-block"
                            @click="openConfirmationDialog"
                            :disabled="disableSubmitButton"
                            :loading="submit_button_loading"
                        >
                            Submit to Twilio
                        </el-button>
                    </div>
                </div>
            </el-form>
        </el-dialog>

        <!-- Failure Reason Dialog -->
        <el-dialog
            :visible.sync="show_failure_reason_dialog"
            :before-close="closeFailureReasonDialog"
            title="Failure Reason"
            width="30%"
        >
            <div class="row">
                <div class="col-12 mb-3">
                    <el-input
                        type="textarea"
                        :rows="4"
                        v-model="failure_reason"
                        placeholder="Enter the reason for rejecting the A2P Campaign"
                    />
                </div>
                <div class="col-12 text-right">
                    <el-button
                        type="danger"
                        @click="rejectA2pCampaign"
                        :disabled="!failure_reason || failure_reason === ''"
                        :loading="reject_button_loading"
                    >
                        Reject
                    </el-button>
                </div>
            </div>
        </el-dialog>

        <!-- Confirmation Dialog -->
        <el-dialog
            :visible.sync="show_confirmation_dialog"
            :before-close="closeConfirmationDialog"
            title="Confirmation"
            width="30%"
        >
            <p>Are you sure you want <b>Submit this A2P Campaign</b> to Twilio?</p>
            <div class="row mt-3">
                <div class="col-12 text-right">
                    <el-button
                        type="primary"
                        @click="submitA2pCampaignToTwilio"
                        :disabled="disableSubmitButton"
                        :loading="submit_button_loading"
                    >
                        Confirm
                    </el-button>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import * as ComplianceBundleStatuses from '../../../constants/compliance-bundle-statuses'
import { compliance_mixin } from '../../../mixins'

export default {
    name: 'A2pCampaignReviewDialog',
    mixins: [compliance_mixin],
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        campaignDetails: {
            type: Object,
            default: null,
        },
        messagingServiceId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            ComplianceBundleStatuses,
            submit_button_loading: false,
            reject_button_loading: false,
            show_confirmation_dialog: false,
            show_failure_reason_dialog: false,
            failure_reason: null,
            campaign_details: null,
        }
    },
    computed: {
        disableSubmitButton() {
            /**
             * NOTICE ON PREVETTING
             * We are currently not using the prevetting process
             */
            return true
        },
        disableRejectButton() {
            /**
             * NOTICE ON PREVETTING
             * We are currently not using the prevetting process
             */
            return true
        },
    },
    watch: {
        campaignDetails: {
            handler(newVal) {
                this.campaign_details = newVal
            },
            immediate: true,
        },
    },
    methods: {
        handleClose() {
            this.$emit('close')
        },
        openFailureReasonDialog() {
            this.show_failure_reason_dialog = true
        },
        closeFailureReasonDialog() {
            this.show_failure_reason_dialog = false
        },
        openConfirmationDialog() {
            this.show_confirmation_dialog = true
        },
        closeConfirmationDialog() {
            this.show_confirmation_dialog = false
        },
        rejectA2pCampaign() {
            this.reject_button_loading = true

            axios
                .post(`/api/admin/compliance/messaging-services/${this.messagingServiceId}/reject-a2p-use-case`, {
                    'failure_reason': this.failure_reason,
                })
                .then((res) => {
                    this.reject_button_loading = false
                    this.$message.success('A2P Campaign Rejected successfully.')
                    this.closeFailureReasonDialog()
                    this.$emit('campaign-updated')
                    this.handleClose()
                })
                .catch((error) => {
                    this.reject_button_loading = false
                    let message = 'Request failed'
                    if (error.response && error.response.data && error.response.data.message) {
                        message = error.response.data.message
                    } else if (error.response && error.response.statusText) {
                        message = error.response.statusText
                    } else if (error.message) {
                        message = error.message
                    }

                    this.$message({
                        message: message,
                        type: 'error',
                    })
                })
        },
        submitA2pCampaignToTwilio() {
            this.submit_button_loading = true

            axios
                .post(`/api/admin/compliance/messaging-services/${this.messagingServiceId}/submit-a2p-use-case`)
                .then((res) => {
                    this.submit_button_loading = false
                    this.closeConfirmationDialog()
                    this.$message.success('A2P Use Case submitted to Twilio successfully.')
                    this.$emit('campaign-updated')
                    this.handleClose()
                })
                .catch((error) => {
                    this.submit_button_loading = false
                    let message = 'Request failed'
                    if (error.response && error.response.data && error.response.data.message) {
                        message = error.response.data.message
                    } else if (error.response && error.response.statusText) {
                        message = error.response.statusText
                    } else if (error.message) {
                        message = error.message
                    }

                    this.$message({
                        message: message,
                        type: 'error',
                    })
                })
        },
    },
}
</script>
